import Backdrop from '@mui/material/Backdrop';
import axios from "axios";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContext } from '.';
import BACKGROUND from './bg';
import GraphicsGalleryPanel from './GraphicsGalleryPanel';
import { TAGS } from './data';
import './editor.scss';
import LazyImage from './LazyImage';
import VirtualizedGrid from './VirtualizedGrid';
import ImageCropper from './imageCropper';
import ImageEditor, { TABS, TOOLS } from "./imageEditor/src";
import HiddenUploadInput from './imageEditor/src/components/common/HiddenUploadInput';
import Spinner from './imageEditor/src/components/common/Spinner';
import MultiSelect from './MultiSelect';
import TextTemplatePickerModal from './TextTemplatePickerModal';
import { TEXT_TEMPLATE_PICKER_ID } from './editorBridgeIds';
import { buildTextTemplateInsertion } from './textTemplateInsertion';
import type { TextTemplateItem } from './textTemplateCatalog';
import { ensureGoogleTextFontsLoaded, TEXT_FONTS } from './textFonts.config';
import {
    applyDocumentUiTheme,
    readStoredUiTheme,
    uiThemeClassName,
    UI_THEME_EVENT,
    normalizeUiTheme,
    type UiTheme,
} from './theme/uiTheme';
import { buildDownloadFileName } from './buildDownloadFileName';
import { downloadBannerAsPdf } from './downloadBannerAsPdf';
import {
    clampFreeFormFrameDimension,
    DEFAULT_FREE_FORM_PRESET,
    FREE_FORM_FRAME_MAX,
    FREE_FORM_FRAME_MIN,
    getBannerTypeDefaults,
    getInitialBgForType,
    getMarketingFramePreset,
    isFreeFormBannerMode,
    MARKETING_FRAME_PRESETS,
    normalizeBannerType,
    type BannerFramePresetId,
} from './bannerFrameTypes';
import {
    createSolidFrameBackground,
    getFreeFormDefaultTextFill,
} from './createSolidFrameBackground';
import {
    analyzeDesignStateForSave,
    isBase64DataUrl,
    prepareDesignStateForSave,
} from './designStateSave';

function AiOutlineCloseCircle(props) {
    return <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path></svg>;
}

function RiImageAddFill2(props) {
    return <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}><g><path fill="none" d="M0 0h24v24H0z" /><path d="M21 15v3h3v2h-3v3h-2v-3h-3v-2h3v-3h2zm.008-12c.548 0 .992.445.992.993v9.349A5.99 5.99 0 0 0 20 13V5H4l.001 14 9.292-9.293a.999.999 0 0 1 1.32-.084l.093.085 3.546 3.55a6.003 6.003 0 0 0-3.91 7.743L2.992 21A.993.993 0 0 1 2 20.007V3.993A1 1 0 0 1 2.992 3h18.016zM8 7a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" /></g></svg>;
}

function HiOutlineArrowsExpand(props) {
    return <svg stroke="currentColor" fill="none" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>;
}

function BiSearchAlt(props) {
    return <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}><path d="M10,18c1.846,0,3.543-0.635,4.897-1.688l4.396,4.396l1.414-1.414l-4.396-4.396C17.365,13.543,18,11.846,18,10 c0-4.411-3.589-8-8-8s-8,3.589-8,8S5.589,18,10,18z M10,4c3.309,0,6,2.691,6,6s-2.691,6-6,6s-6-2.691-6-6S6.691,4,10,4z" /><path d="M11.412,8.586C11.791,8.966,12,9.468,12,10h2c0-1.065-0.416-2.069-1.174-2.828c-1.514-1.512-4.139-1.512-5.652,0 l1.412,1.416C9.346,7.83,10.656,7.832,11.412,8.586z" /></svg>;
}

// const RESPARK_LOGO ="";
const RESPARK_LOGO = "https://pcs-s3.s3.ap-south-1.amazonaws.com/3/logo/2022-09-15T14%3A57%3A13.639213_slider.png";
const DEVOURIN_LOGO = "https://devourin.com/wp-content/uploads/2023/07/Group-1161.png";

const defaultConfig: any = {
    tenantId: '',
    storeId: '',
    type: '',
    height: '',
    width: '',
    aspectRatio: '',
    group: '',
    from: '',
    baseURL: '',
    logo: RESPARK_LOGO
}

const TEMPLATE_TYPE = {
    PLATFORM: "PLATFORM",
    USER: "USER"
}

/** Pick the first defined (non-null / non-undefined) value from candidates. */
const firstDefined = (...values: any[]) =>
    values.find((value) => value !== undefined && value !== null);

/**
 * Normalize host/dashboard Editor Config so campaign API headers always have
 * Entity + TenantId + StoreId (+ AppName). Dashboards historically use mixed
 * casings (`TenantId` vs `tenantId`) and may omit fields.
 */
function normalizeIncomingEditorConfig(raw: any = {}) {
    const config = { ...(raw || {}) };

    const tenantId = firstDefined(
        config.tenantId,
        config.TenantId,
        config.tenant_id,
        config.t_id,
    );
    const storeId = firstDefined(
        config.storeId,
        config.StoreId,
        config.store_id,
        config.s_id,
    );
    const entity = firstDefined(config.entity, config.Entity);
    const appName = firstDefined(config.appName, config.AppName, config.app_name);

    // Empty string is treated as missing — API rejects blank tenantid/storeid headers.
    config.tenantId =
        tenantId === undefined || tenantId === '' ? 0 : tenantId;
    config.storeId =
        storeId === undefined || storeId === '' ? 0 : storeId;
    config.entity =
        entity === undefined || entity === '' ? 'RESPARK' : String(entity).toUpperCase();
    config.appName =
        appName === undefined || appName === '' ? 'DEFAULT' : appName;

    if (!config.logo) {
        config.logo = config.entity === 'DEVOURIN' ? DEVOURIN_LOGO : RESPARK_LOGO;
    }

    return config;
}

/**
 * Campaign API requires non-empty tenantid, storeid, entity (+ appName).
 * Never send blank header values — that returns
 * "Header Required (Entity … & StoreId & TenantId or AppName)".
 */
function buildCampaignAxiosHeaders(config: any = {}, isRespark = false) {
    const tenantRaw = isRespark ? '0' : firstDefined(config.tenantId, config.TenantId, 0);
    const storeRaw = isRespark ? '0' : firstDefined(config.storeId, config.StoreId, 0);
    const tenantid = tenantRaw === '' || tenantRaw === undefined || tenantRaw === null
        ? '0'
        : String(tenantRaw);
    const storeid = storeRaw === '' || storeRaw === undefined || storeRaw === null
        ? '0'
        : String(storeRaw);
    const entityRaw = firstDefined(config.entity, config.Entity, 'RESPARK');
    const entity =
        entityRaw === '' || entityRaw == null ? 'RESPARK' : String(entityRaw).toUpperCase();
    const appNameRaw = firstDefined(config.appName, config.AppName, 'DEFAULT');
    const appName =
        appNameRaw === '' || appNameRaw == null ? 'DEFAULT' : String(appNameRaw);

    return {
        headers: {
            tenantid,
            storeid,
            entity,
            appName,
        },
    };
}

const parseCommaList = (value?: string) =>
    (value || '').split(',').map((item) => item.trim()).filter(Boolean);

const mergeUniqueSortedCategories = (...categoryLists: string[][]) => {
    const seen = new Map<string, string>();

    categoryLists.flat().forEach((category) => {
        const trimmed = category.trim();
        if (!trimmed) {
            return;
        }
        const key = trimmed.toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, trimmed);
        }
    });

    return Array.from(seen.values()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
};

const templateMatchesCategory = (
    templateCategory: string | undefined,
    filter: string,
) => {
    if (filter === 'All') {
        return true;
    }
    if (!templateCategory) {
        return false;
    }
    const filterLower = filter.toLowerCase();
    return templateCategory
        .split(',')
        .map((category) => category.trim().toLowerCase())
        .includes(filterLower);
};

const getTemplateCanvasConfig = (templateData: any, prevConfig: any) => {
    const dims = templateData?.designState?.shownImageDimensions;
    if (dims?.width && dims?.height) {
        const width = Math.round(dims.width);
        const height = Math.round(dims.height);
        return {
            ...prevConfig,
            width,
            height,
            aspectRatio: width / height,
            ...(templateData.type ? { type: templateData.type } : {}),
        };
    }

    if (templateData?.type) {
        const typeKey = String(templateData.type).toLowerCase();
        const defaults = getBannerTypeDefaults(typeKey);
        if (defaults) {
            return { ...prevConfig, ...defaults, type: templateData.type };
        }
        if (isFreeFormBannerMode(typeKey)) {
            const preset = getMarketingFramePreset(
                templateData.framePreset || DEFAULT_FREE_FORM_PRESET,
            );
            return {
                ...prevConfig,
                type: normalizeBannerType(typeKey),
                framePreset: templateData.framePreset || DEFAULT_FREE_FORM_PRESET,
                ...(preset
                    ? {
                        width: preset.width,
                        height: preset.height,
                        aspectRatio: preset.aspectRatio,
                    }
                    : {}),
            };
        }
    }

    return null;
};

function getStandaloneDevDefaultConfig() {
    const preset =
        getMarketingFramePreset(DEFAULT_FREE_FORM_PRESET) ||
        MARKETING_FRAME_PRESETS[0];
    return {
        tenantId: 0,
        storeId: 0,
        entity: 'RESPARK',
        appName: 'DEFAULT',
        type: normalizeBannerType(null),
        width: preset.width,
        height: preset.height,
        aspectRatio: preset.aspectRatio,
        framePreset: DEFAULT_FREE_FORM_PRESET,
        frameBgKind: 'solid',
        group: 'both',
        from: 'Slider',
        title: 'Update slider banner!',
        logo: defaultConfig.logo,
    };
}

function applyUrlConfigOverrides(config: any) {
    const params = new URLSearchParams(window.location.search);
    if (!window.location.search) {
        return config;
    }
    if (params.get('t_id')) {
        config.tenantId = params.get('t_id');
    }
    if (params.get('s_id')) {
        config.storeId = params.get('s_id');
    }
    if (params.get('type')) {
        config.type = params.get('type');
        const typeKey = String(config.type).toLowerCase();
        if (isFreeFormBannerMode(typeKey)) {
            config.type = normalizeBannerType(typeKey);
            config.framePreset = config.framePreset || DEFAULT_FREE_FORM_PRESET;
            config.frameBgKind = config.frameBgKind || 'solid';
        }
        if (!params.get('w') && !params.get('h')) {
            const defaults = getBannerTypeDefaults(typeKey);
            if (defaults) {
                config.width = defaults.width;
                config.height = defaults.height;
                config.aspectRatio = defaults.aspectRatio;
            } else if (isFreeFormBannerMode(typeKey)) {
                const preset = getMarketingFramePreset(config.framePreset);
                if (preset) {
                    config.width = preset.width;
                    config.height = preset.height;
                    config.aspectRatio = preset.aspectRatio;
                }
            }
        }
    }
    if (params.get('entity')) {
        config.entity = params.get('entity');
        config.logo = config.entity === 'DEVOURIN' ? DEVOURIN_LOGO : RESPARK_LOGO;
    }
    if (params.get('w')) {
        config.width = Number(params.get('w'));
    }
    if (params.get('h')) {
        config.height = Number(params.get('h'));
    }
    if (params.get('r')) {
        config.aspectRatio = Number(params.get('r'));
    }
    return config;
}

/**
 * One-off backfill: migrate every template's `designState.imgSrc` from inline base64 to an
 * S3 URL. Parameterless — edit the BACKFILL_CONFIG constants below, then call
 * `backfillBackgroundUrls()` (e.g. from a temporary button or the browser console).
 *
 * For each configured entity it lists templates per type, fetches each detail (the list omits
 * imgSrc), uploads any base64 background to `s3/uploadwithtype`, and removes unedited
 * placeholder text annotations (text === "Add text here"), then re-saves the template. A row is
 * re-saved if its background needs migrating OR it has placeholder text to drop; rows needing
 * neither are skipped. Set dryRun:true to preview first.
 */
export async function backfillBackgroundUrls() {
    const BACKFILL_CONFIG = {
        campaignBaseUrl: process.env.REACT_APP_CAMPAING_BASE_URL || '',
        baseUrl: process.env.REACT_APP_BASE_URL || '', // must end with '/'
        appName: 'DEFAULT',
        types: ['small_banner', 'large_banner', 'square_banner'],
        // Run for each audience. Platform/Respark templates use tenant/store "0".
        audiences: [
            { entity: 'RESPARK', tenantId: '0', storeId: '0' },
            { entity: 'DEVOURIN', tenantId: '0', storeId: '0' },
        ],
        dryRun: true,
    };

    if (!BACKFILL_CONFIG.campaignBaseUrl || !BACKFILL_CONFIG.baseUrl) {
        console.error('[backfill] Missing REACT_APP_CAMPAING_BASE_URL / REACT_APP_BASE_URL.');
        return;
    }

    const buildHeaders = (a: { entity: string; tenantId: string; storeId: string }) => ({
        headers: {
            tenantid: a.tenantId,
            storeid: a.storeId,
            entity: a.entity,
            appName: BACKFILL_CONFIG.appName,
        },
    });

    const stats = { scanned: 0, updated: 0, placeholdersRemoved: 0, skipped: 0, failed: 0 };
    console.log(`[backfill] start${BACKFILL_CONFIG.dryRun ? ' (DRY RUN)' : ''}`);

    for (const audience of BACKFILL_CONFIG.audiences) {
        const headers = buildHeaders(audience);
        for (const type of BACKFILL_CONFIG.types) {
            let list: any[] = [];
            try {
                const res = await axios.get(
                    `${BACKFILL_CONFIG.campaignBaseUrl}?type=${encodeURIComponent(type)}`,
                    headers,
                );
                list = res.data?.data || [];
            } catch (e: any) {
                console.warn(`[backfill] list ${audience.entity}/${type} failed: ${e?.message || e}`);
                continue;
            }
            console.log(`[backfill] ${audience.entity}/${type}: ${list.length} templates`);

            for (const item of list) {
                stats.scanned += 1;
                try {
                    const detailRes = await axios.get(
                        `${BACKFILL_CONFIG.campaignBaseUrl}/${item.id}`,
                        headers,
                    );
                    const detail = detailRes.data?.data;
                    const designState = detail?.designState;
                    const analysis = analyzeDesignStateForSave(designState);

                    if (!analysis.needsSave) {
                        stats.skipped += 1;
                        continue;
                    }
                    if (BACKFILL_CONFIG.dryRun) {
                        stats.updated += 1;
                        stats.placeholdersRemoved += analysis.placeholdersRemoved;
                        console.log(
                            `  - ${item.id}: WOULD update (bg=${analysis.needsBgUpload ? 'base64→url' : 'unchanged'}, ` +
                            `placeholders removed=${analysis.placeholdersRemoved})`,
                        );
                        continue;
                    }

                    const prepared = await prepareDesignStateForSave(
                        designState,
                        BACKFILL_CONFIG.baseUrl,
                    );
                    if (
                        analysis.needsBgUpload &&
                        isBase64DataUrl(prepared.designState?.imgSrc)
                    ) {
                        throw new Error('upload returned no URL');
                    }
                    detail.designState = prepared.designState;
                    await axios.post(BACKFILL_CONFIG.campaignBaseUrl, detail, headers);
                    stats.updated += 1;
                    stats.placeholdersRemoved += prepared.placeholdersRemoved;
                    console.log(
                        `  - ${item.id}: updated (bg=${analysis.needsBgUpload ? 'uploaded' : 'unchanged'}, ` +
                        `placeholders removed=${prepared.placeholdersRemoved})`,
                    );
                } catch (e: any) {
                    stats.failed += 1;
                    console.warn(`  - ${item.id}: FAILED (${e?.message || e})`);
                }
            }
        }
    }

    console.log(
        `[backfill] done. scanned=${stats.scanned} updated=${stats.updated} ` +
        `placeholdersRemoved=${stats.placeholdersRemoved} skipped=${stats.skipped} failed=${stats.failed}`,
    );
    return stats;
}

const ADDED_IMG_SPACING_PERCENT = 0.15;
const DEFAULT_INITIAL_BG = `${process.env.PUBLIC_URL || ''}/initial-bg.png`;

function flattenBackgroundImages(
    backgrounds: Record<string, string[]> | undefined,
    activeCategory: string,
): { key: string; src: string }[] {
    if (!backgrounds) {
        return [];
    }
    if (activeCategory === 'All') {
        return Object.values(backgrounds).flatMap((urls) =>
            urls.map((src) => ({ key: src, src })),
        );
    }
    return (backgrounds[activeCategory] || []).map((src) => ({ key: src, src }));
}

function Editor({ props }: any = {}) {
    useEffect(() => {
        ensureGoogleTextFontsLoaded();
    }, []);
    const [showEditor, setShowEditor] = useState(true);
    const [editorKey, setEditorKey] = useState(0);
    const [uiTheme, setUiTheme] = useState<UiTheme>(() => readStoredUiTheme());

    useEffect(() => {
        applyDocumentUiTheme(uiTheme);
    }, [uiTheme]);

    useEffect(() => {
        const onThemeChange = (event: Event) => {
            setUiTheme(normalizeUiTheme((event as CustomEvent).detail));
        };
        window.addEventListener(UI_THEME_EVENT, onThemeChange);
        return () => window.removeEventListener(UI_THEME_EVENT, onThemeChange);
    }, []);
    const [bgImage, setBgImage] = useState<any>(DEFAULT_INITIAL_BG);
    const [resparkTemplates, setResparkTemplates] = useState<any[]>([]);
    const [templatesList, setTemplatesList] = useState<any[]>([]);
    const [activeTemplate, setActiveTemplate] = useState<any>(null);
    const [showBgImages, setShowBgImages] = useState(false)
    const [showImagesPanel, setShowImagesPanel] = useState(false)
    const [showExpandedTemplates, setShowExpandedTemplates] = useState(false)
    const [oldBgImage, setOldBgImage] = useState(DEFAULT_INITIAL_BG);
    const [isLoading, setIsLoading] = useState(false)
    const bgImageModalRef = useRef<any>(null);
    const testStyleModalRef = useRef<any>(null);
    const editorWrapRef = useRef<any>(null);
    const cropperRef = useRef<any>(null);
    const initiatedRef = useRef(false);
    const freeFormHasCanvasEditsRef = useRef(false);
    const [showTemplateChangeConfirmation, setShowTemplateChangeConfirmation] = useState<any>({ active: false, template: {}, userAgreeToChange: false });
    const [showTemplateDeleteConfirmation, setShowTemplateDeleteConfirmation] = useState<any>({ active: false, template: {}, userAgreeToChange: false, templateIndex: null });
    const [showTemplateEditModal, setShowTemplateEditModal] = useState<any>({ active: false, template: {}, templateIndex: null });
    const uploadImgsInput = useRef();
    const uploadGraphicsInput = useRef<any>();
    const [activeBgImgCategory, setActiveBgImgCategory] = useState('All');
    const [activeGraphicsCategory, setActiveGraphicsCategory] = useState('All');
    const [isImportingGraphics, setIsImportingGraphics] = useState(false);
    const [showAddTextModal, setShowAddTextModal] = useState(false);
    const [editorConfig, setEditorConfig] = useState<any>(defaultConfig);
    const editedImage = useRef<
        (
            imageFileInfo?: object,
            pixelRatio?: boolean | number,
            keepLoadingSpinnerShown?: boolean,
            exportOptions?: { extension?: string; transparentBackground?: boolean },
        ) => { imageData: object; designState: object; hideLoadingSpinner: (...args: any[]) => any }
    >();
    // Live bridge into the editor's reducer (UPDATE_STATE). Lets us patch design
    // state (e.g. add a text annotation) without unmounting/remounting the canvas.
    const updateEditorState = useRef<(payloadObjOrFn: any) => void>();
    const selectEditorTab = useRef<(tabId: string) => void>();
    const keyboardShortcutHandlersRef = useRef<{ openSaveMenu?: () => void }>({});
    const [showCropper, setShowCropper] = useState({ active: false, img: '' });
    const [pendingCropOutputSize, setPendingCropOutputSize] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const [parentConfigMesg, setParentConfigMesg] = useState<any>({});
    const { currentTab, currentId, currentAction, setCurrentAction, setActiveTemplateContext, setCurrentId, setEditorConfigContext, setCurrentTab } = useContext(EditorContext);
    const [waterMarks, setWaterMarks] = useState('');
    const [templateType, setTemplateType] = useState(TEMPLATE_TYPE.PLATFORM)
    const [templateCategories, setTemplateCategories] = useState<string[]>([]);
    const [templateFilters, setTemplateFilters] = useState({ searchTerm: "", category: "All" })
    const [templatesScrollRoot, setTemplatesScrollRoot] = useState<HTMLDivElement | null>(null);
    const [templatesListWidth, setTemplatesListWidth] = useState(0);
    const [bgScrollRoot, setBgScrollRoot] = useState<HTMLDivElement | null>(null);

    const templateColumnCount = showExpandedTemplates ? 4 : 1;
    const templateColGapPx = templateColumnCount > 1 ? 10 : 5;
    const templateRowGap = templateColumnCount > 1 ? 10 : 4;
    const templateThumbDimensions = useMemo(() => {
        const configWidth = Number(editorConfig.width);
        const configHeight = Number(editorConfig.height);
        if (configWidth > 0 && configHeight > 0) {
            return { width: configWidth, height: configHeight };
        }
        const defaults = getBannerTypeDefaults(String(editorConfig.type || 'small_banner').toLowerCase());
        if (defaults) {
            return { width: defaults.width, height: defaults.height };
        }
        return { width: 413, height: 284 };
    }, [editorConfig.width, editorConfig.height, editorConfig.type]);
    const templateThumbAspectRatio = `${templateThumbDimensions.width} / ${templateThumbDimensions.height}`;
    const templateThumbHeightRatio =
        templateThumbDimensions.height / templateThumbDimensions.width;
    const templateListWidthForEstimate = useMemo(() => {
        const collapsedFallback = 238;
        const expandedMinWidth = 320;

        if (!showExpandedTemplates) {
            // After collapse, scroll root can still report expanded width for a frame.
            if (templatesListWidth > 0 && templatesListWidth <= expandedMinWidth) {
                return templatesListWidth;
            }
            return collapsedFallback;
        }

        if (templatesListWidth > expandedMinWidth) {
            return templatesListWidth;
        }
        return Math.max(window.innerWidth - 170, 640);
    }, [templatesListWidth, showExpandedTemplates]);
    const templateEstimatedRowHeight = useMemo(() => {
        const colGap = templateColGapPx * (templateColumnCount - 1);
        const cardWidth = (templateListWidthForEstimate - colGap) / templateColumnCount;
        return Math.ceil(cardWidth * templateThumbHeightRatio);
    }, [
        templateListWidthForEstimate,
        templateColumnCount,
        templateColGapPx,
        templateThumbHeightRatio,
    ]);

    useEffect(() => {
        const el = templatesScrollRoot;
        if (!el) {
            setTemplatesListWidth(0);
            return undefined;
        }
        const update = () => setTemplatesListWidth(el.clientWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [templatesScrollRoot, showExpandedTemplates]);

    useEffect(() => {
        if (!templatesScrollRoot) {
            return;
        }
        templatesScrollRoot.scrollTop = 0;
    }, [showExpandedTemplates, templatesScrollRoot]);

    useEffect(() => {
        setEditorConfigContext(editorConfig);
    }, [editorConfig, setEditorConfigContext]);

    useEffect(() => {
        if (activeTemplate) {
            setActiveTemplateContext(activeTemplate);
        }
    }, [activeTemplate, setActiveTemplateContext]);

    useEffect(() => {
        setShowBgImages(false);
        setShowImagesPanel(false);
        setShowAddTextModal(false);
        if (currentTab !== TABS.TEMPLATE) {
            setShowExpandedTemplates(false);
        }
        if (currentTab === TABS.BACKGROUND) {
            setShowBgImages(true);
        } else if (currentTab === TABS.IMAGE_GALLERY) {
            setShowImagesPanel(true);
        }
    }, [currentTab, currentId])

    useEffect(() => {
        if (currentTab !== TABS.TEMPLATE || !activeTemplate?.id) {
            return;
        }
        const inUser = templatesList.some((t: any) => t.id === activeTemplate.id);
        const inPlatform = resparkTemplates.some((t: any) => t.id === activeTemplate.id);
        if (inUser && !inPlatform) {
            setTemplateType(TEMPLATE_TYPE.USER);
        } else if (inPlatform) {
            setTemplateType(TEMPLATE_TYPE.PLATFORM);
        }
    }, [currentTab, activeTemplate?.id, templatesList, resparkTemplates])

    useEffect(() => {
        if (currentAction?.type === 'save' && currentAction?.action) handleSaveButtonClick(currentAction?.action);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- save handler is stable; only re-run when parent posts save action
    }, [currentAction])

    useEffect(() => {
        if (currentId === TEXT_TEMPLATE_PICKER_ID) {
            setShowAddTextModal(true);
        }
    }, [currentId])

    useEffect(() => {
        if (!showAddTextModal) setCurrentId('');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- clear tool id when modal closes; setCurrentId is stable
    }, [showAddTextModal])

    useEffect(() => {
        const handleParentMessage = (e: MessageEvent) => {
            if (e.data?.type === 'Editor Config' && !initiatedRef.current) {
                initiatedRef.current = true;
                setParentConfigMesg(e.data.data);
            }
        };

        if (window.location.ancestorOrigins.length) {//this means editor opens in iframe
            window.parent.postMessage({ type: 'Editor Mounted', data: 'true' }, window.location.ancestorOrigins[0]);
            window.addEventListener('message', handleParentMessage);
        } else {
            const config = sessionStorage.getItem('editor__config');
            if (config) {
                setParentConfigMesg(JSON.parse(config));
            } else {
                setParentConfigMesg(getStandaloneDevDefaultConfig());
            }
        }

        // Close dropdown when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.querySelector('.options-list');
            const container = document.querySelector('.multi-select-dropdown');
            if (dropdown && container && !container.contains(event.target as Node)) {
                dropdown.classList.remove('show');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('message', handleParentMessage);
            document.removeEventListener('mousedown', handleClickOutside);
        };

    }, [])

    useEffect(() => {
        if (parentConfigMesg && Object.keys(parentConfigMesg).length > 0) {
            const config = normalizeIncomingEditorConfig(
                applyUrlConfigOverrides({ ...parentConfigMesg }),
            );
            config.type = normalizeBannerType(config.type);
            if (isFreeFormBannerMode(config.type)) {
                config.framePreset = config.framePreset || DEFAULT_FREE_FORM_PRESET;
                config.frameBgKind = config.frameBgKind || 'solid';
                const preset = getMarketingFramePreset(config.framePreset);
                const frameW =
                    config.width ||
                    preset?.width ||
                    getMarketingFramePreset(DEFAULT_FREE_FORM_PRESET)?.width ||
                    1080;
                const frameH =
                    config.height ||
                    preset?.height ||
                    getMarketingFramePreset(DEFAULT_FREE_FORM_PRESET)?.height ||
                    1080;
                config.width = frameW;
                config.height = frameH;
                config.aspectRatio = frameW / frameH;
            }
            setEditorConfig(config)
            localStorage.setItem("r-e-c", JSON.stringify(config))
            const bootBgOverride = isFreeFormBannerMode(config.type)
                ? createSolidFrameBackground(config.width, config.height, uiTheme)
                : getInitialBgForType(config.type, BACKGROUND, DEFAULT_INITIAL_BG);
            commitActiveTemplate(getEmptyTemplateObj(), {
                remount: false,
                bgOverride: bootBgOverride,
            });
            if (isFreeFormBannerMode(config.type)) {
                freeFormHasCanvasEditsRef.current = false;
                setCurrentTab(TABS.ANNOTATE);
            } else {
                getTemplatesData(config);

                if (resparkTemplates.length === 0) {
                    axios.get(`${process.env.REACT_APP_CAMPAING_BASE_URL}?type=${config.type}`, getAxiosHeaders(config, true)).then((response: any) => {
                        let templatesList = []
                        templatesList = response.data?.data || [];
                        setResparkTemplates(templatesList);
                        setIsLoading(false);
                        getCategories(templatesList)
                        if (parentConfigMesg.templateId) {
                            setTimeout(() => {
                                let isAvl = templatesList.filter((t: any) => t.id === parentConfigMesg.templateId);
                                if (isAvl.length !== 0) {
                                    getAndUpdateActiveTemplateByTemplateId(isAvl[0].id, config)
                                }
                            });
                        }
                    }).catch((e) => {
                        setIsLoading(false);
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap from parent config once; expanding deps causes duplicate fetches
    }, [parentConfigMesg])

    const getCategories = (templatesList) => {
        const categoriesFromTemplates = templatesList.flatMap((template) =>
            parseCommaList(template.category),
        );
        setTemplateCategories(mergeUniqueSortedCategories(categoriesFromTemplates));
    }
    const getAxiosHeaders = (config, isRespark = false) =>
        buildCampaignAxiosHeaders(config, isRespark);

    // Fetch the logo as a blob and convert to a data URL. Using fetch (instead of an
    // <img crossorigin>) avoids the classic S3 tainted-cache trap where a plain <img>
    // caches the response without CORS headers and a later crossOrigin request reuses
    // that cached (non-CORS) response and fails.
    function getBase64Image(url: string): Promise<string> {
        return fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error(`Logo fetch failed: ${response.status}`);
                return response.blob();
            })
            .then(
                (blob) =>
                    new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    }),
            );
    }
    useEffect(() => {
        if (!waterMarks && editorConfig.type && editorConfig.logo) {
            getBase64Image(editorConfig.logo)
                .then((base64) => setWaterMarks(base64))
                .catch(() => {
                    // Watermark is optional; fail silently so it never blanks the editor.
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load watermark once per editorConfig identity
    }, [editorConfig])

    const getTemplatesData = (config: any) => {
        if (config.tenantId !== 0) {
            setIsLoading(true);
            axios.get(`${process.env.REACT_APP_CAMPAING_BASE_URL}?type=${config.type}`, getAxiosHeaders(config))
                .then((response: any) => {
                    let templatesList = []
                    templatesList = response.data?.data || [];
                    setIsLoading(false);
                    setTemplatesList(templatesList);
                    if (parentConfigMesg.templateId) {
                        let isAvl = templatesList.filter((t: any) => t.id === parentConfigMesg.templateId);
                        if (isAvl.length !== 0) {
                            getAndUpdateActiveTemplateByTemplateId(isAvl[0].id, config)
                        }
                    }
                })
                .catch((e) => {
                    setIsLoading(false);
                });
        }
    }

    // Apply template + background together, then remount. Doing this in one batch
    // avoids a render where loadableDesignState is new but source/bgImage is still
    // the old initial background (that mismatch blanked the canvas on localhost).
    const commitActiveTemplate = (
        templateData: any,
        options?: { remount?: boolean; bgOverride?: any },
    ) => {
        const nextBg =
            options?.bgOverride ?? templateData?.designState?.imgSrc;
        const nextConfig = getTemplateCanvasConfig(templateData, editorConfig);
        if (nextConfig) {
            setEditorConfig(nextConfig);
        }
        if (!nextBg) {
            setActiveTemplate({ ...templateData });
            return;
        }
        setOldBgImage(nextBg);
        setBgImage(nextBg);
        setActiveTemplate({ ...templateData });
        if (options?.remount !== false) {
            setEditorKey((k) => k + 1);
        }
    };

    const getEmptyTemplateObj = () => {
        return {
            "designState": {},
            "image": "",
            "index": 1,
            "active": true,
            "group": "both",
            "category": "",
            "storeId": editorConfig.storeId === '' || editorConfig.storeId == null
                ? 0
                : editorConfig.storeId,
            "tenantId": editorConfig.tenantId === '' || editorConfig.tenantId == null
                ? 0
                : editorConfig.tenantId,
            "type": editorConfig.type,
            "entity": editorConfig.entity || 'RESPARK',
        }
    }

    const getAndUpdateActiveTemplateByTemplateId = (templateId: any, config = editorConfig) => {
        setIsLoading(true);
        const isResparkTemplate = resparkTemplates.find((t: any) => t.id === templateId);
        axios.get(`${process.env.REACT_APP_CAMPAING_BASE_URL}/${templateId}`, getAxiosHeaders(config, Boolean(isResparkTemplate))).then(async (res: any) => {
            const templateData = res.data?.data;
            const imgSrc = templateData?.designState?.imgSrc;
            if (imgSrc) {
                // Backgrounds are now stored as URLs, but the editor needs base64 (avoids
                // the render-gate / canvas-taint failure modes). Fetch + convert remote
                // sources; legacy templates already hold a data: URL, so pass those through.
                if (/^https?:\/\//i.test(imgSrc)) {
                    try {
                        templateData.designState.imgSrc = await urlToBase64(imgSrc);
                    } catch {
                        // Keep remote URL if conversion fails.
                    }
                }
                setIsLoading(false);
                commitActiveTemplate(templateData);
            } else {
                setIsLoading(false);
            }
        }).catch(() => {
            setIsLoading(false);
        });
    }

    const onSelectTemplate = (template: any) => {
        if (template) {
            if (!activeTemplate.id || showTemplateChangeConfirmation.userAgreeToChange || activeTemplate.id === template.id) {
                getAndUpdateActiveTemplateByTemplateId(template.id);
            } else {
                setShowTemplateChangeConfirmation({ ...showTemplateChangeConfirmation, template, active: true });
            }
        } else if (isFreeFormBannerMode(editorConfig.type)) {
            const preset =
                getMarketingFramePreset(DEFAULT_FREE_FORM_PRESET) ||
                MARKETING_FRAME_PRESETS[0];
            applyFreeFormFrame(preset.width, preset.height, preset.id);
        } else {
            commitActiveTemplate(getEmptyTemplateObj(), {
                bgOverride: getInitialBgForType(
                    editorConfig.type,
                    BACKGROUND,
                    DEFAULT_INITIAL_BG,
                ),
            });
        }
        setShowExpandedTemplates(false)
    }

    const replaceTemplate = () => {
        getAndUpdateActiveTemplateByTemplateId(showTemplateChangeConfirmation.template?.id)
        setShowTemplateChangeConfirmation({ ...showTemplateChangeConfirmation, active: false, template: {} })
    }

    const applyFreeFormFrame = useCallback(
        (width: number, height: number, presetId: BannerFramePresetId) => {
            if (!isFreeFormBannerMode(editorConfig.type)) {
                return;
            }
            const w = clampFreeFormFrameDimension(width);
            const h = clampFreeFormFrameDimension(height);
            if (w < FREE_FORM_FRAME_MIN || h < FREE_FORM_FRAME_MIN) {
                return;
            }
            const solidBg = createSolidFrameBackground(w, h, uiTheme);
            setEditorConfig((prev: any) => ({
                ...prev,
                width: w,
                height: h,
                aspectRatio: w / h,
                framePreset: presetId,
                frameBgKind: 'solid',
            }));
            setOldBgImage(solidBg);
            setBgImage(solidBg);
            updateEditorState.current?.({
                isDesignState: true,
                resize: { width: w, height: h, manualChangeDisabled: false },
            });
        },
        [editorConfig.type, uiTheme],
    );

    const openBgCropper = (
        img: string,
        options: {
            outputSize?: { width: number; height: number } | null;
        } = {},
    ) => {
        setPendingCropOutputSize(options.outputSize ?? null);
        setShowCropper({ active: true, img });
    };

    const handleBannerFrameSelect = useCallback(
        (presetId: string) => {
            const preset = getMarketingFramePreset(presetId);
            if (!preset) {
                return;
            }
            applyFreeFormFrame(preset.width, preset.height, preset.id);
        },
        [applyFreeFormFrame],
    );

    const handleBannerFrameCustomSize = useCallback(
        (width: number, height: number) => {
            applyFreeFormFrame(width, height, 'custom');
        },
        [applyFreeFormFrame],
    );

    const isFreeFormEditor = isFreeFormBannerMode(editorConfig?.type);

    const defaultTextFill = useMemo(
        () =>
            isFreeFormEditor ? getFreeFormDefaultTextFill(uiTheme) : '#000000',
        [isFreeFormEditor, uiTheme],
    );

    const sidebarTabsIds = useMemo(() => {
        const coreTabs = [
            TABS.ANNOTATE,
            TABS.IMAGE_GALLERY,
            TABS.ADJUST,
            // TABS.FILTERS, // Effects tab hidden — filters panel code kept for future use
        ];
        if (isFreeFormEditor) {
            return coreTabs;
        }
        return [TABS.TEMPLATE, TABS.BACKGROUND, ...coreTabs];
    }, [isFreeFormEditor]);

    useEffect(() => {
        if (!isFreeFormEditor) {
            return;
        }
        setShowBgImages(false);
        setShowExpandedTemplates(false);
        if (currentTab === TABS.TEMPLATE || currentTab === TABS.BACKGROUND) {
            setCurrentTab(TABS.ANNOTATE);
            selectEditorTab.current?.(TABS.ANNOTATE);
        }
    }, [isFreeFormEditor, currentTab, setCurrentTab]);

    const bannerFrameControls = useMemo(
        () => ({
            enabled: isFreeFormEditor,
            width: editorConfig?.width,
            height: editorConfig?.height,
            framePreset: editorConfig?.framePreset || DEFAULT_FREE_FORM_PRESET,
            presets: MARKETING_FRAME_PRESETS,
            minDimension: FREE_FORM_FRAME_MIN,
            maxDimension: FREE_FORM_FRAME_MAX,
            onSelectPreset: handleBannerFrameSelect,
            onApplyCustomSize: handleBannerFrameCustomSize,
        }),
        [
            isFreeFormEditor,
            editorConfig?.width,
            editorConfig?.height,
            editorConfig?.framePreset,
            handleBannerFrameSelect,
            handleBannerFrameCustomSize,
        ],
    );

    const handleFreeFormDesignModify = useCallback((state: any) => {
        if (!isFreeFormBannerMode(editorConfig?.type)) {
            return;
        }
        const annotationCount = Object.keys(state?.annotations || {}).length;
        if (annotationCount > 0) {
            freeFormHasCanvasEditsRef.current = true;
        }
    }, [editorConfig?.type]);

    const prevUiThemeForFrameRef = useRef(uiTheme);
    useEffect(() => {
        if (!isFreeFormBannerMode(editorConfig?.type)) {
            prevUiThemeForFrameRef.current = uiTheme;
            return undefined;
        }
        if (editorConfig?.frameBgKind !== 'solid') {
            prevUiThemeForFrameRef.current = uiTheme;
            return undefined;
        }
        if (prevUiThemeForFrameRef.current === uiTheme) {
            return undefined;
        }
        prevUiThemeForFrameRef.current = uiTheme;
        if (freeFormHasCanvasEditsRef.current) {
            return undefined;
        }
        const w = editorConfig?.width;
        const h = editorConfig?.height;
        if (!w || !h) {
            return undefined;
        }
        const solidBg = createSolidFrameBackground(w, h, uiTheme);
        setOldBgImage(solidBg);
        setBgImage(solidBg);
        return undefined;
    }, [uiTheme, editorConfig?.type, editorConfig?.frameBgKind, editorConfig?.width, editorConfig?.height]);

    const onChangeBg = (nextBg: any) => {
        if (nextBg) {
            setBgImage(nextBg);
            // Prior template crop (x/y/width/height) otherwise clips the new background
            // — that showed up as left/right content cut off after upload + save.
            updateEditorState.current?.((state: any) => ({
                isDesignState: true,
                adjustments: {
                    ...state.adjustments,
                    crop: {
                        ratio: state.adjustments?.crop?.ratio ?? 'original',
                        ratioTitleKey:
                            state.adjustments?.crop?.ratioTitleKey ?? 'original',
                        width: null,
                        height: null,
                        x: 0,
                        y: 0,
                    },
                },
            }));
        } else {
            setBgImage(oldBgImage);
        }
    };

    const getBgCropOutputSize = () => {
        const w = Number(editorConfig?.width);
        const h = Number(editorConfig?.height);
        if (!w || !h) {
            return null;
        }
        // 2× banner pixels: sharp enough for text, bounded so exports stay ~hundreds of KB
        // instead of multi‑MB full-screenshot PNGs.
        return { width: Math.round(w * 2), height: Math.round(h * 2) };
    };

    const JPEG_EXPORT_OPTIONS = {
        extension: 'jpeg' as const,
        transparentBackground: false,
    };
    const JPEG_BLOB_QUALITY = 0.92;

    /** Same raster path as Download JPEG: extension jpeg + savingPixelRatio (2×). */
    const exportJpegInstance = () =>
        getEditorInstance(JPEG_EXPORT_OPTIONS, {}, false);

    const uploadJpegCanvasToS3 = (imageCanvas: HTMLCanvasElement) =>
        new Promise<any>((resolve, reject) => {
            imageCanvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to create JPEG blob'));
                        return;
                    }
                    const formData = new FormData();
                    formData.append('file', blob, `${Math.random()}_banner.jpg`);
                    formData.append('id', '0');
                    formData.append('type', 'promotions');
                    setIsLoading(true);
                    axios
                        .post(`${process.env.REACT_APP_BASE_URL}s3/uploadwithtype`, formData)
                        .then((res: any) => {
                            setIsLoading(false);
                            resolve(res.data);
                        })
                        .catch((e) => {
                            setIsLoading(false);
                            reject(e);
                        });
                },
                'image/jpeg',
                JPEG_BLOB_QUALITY,
            );
        });

    const handleSaveButtonClick = (event: any) => {
        if (event) {
            if (event === 'add') {
                addNewTemplate();
            } else if (event === 'update') {
                updateTemplate();
            } else if (
                event === 'download' ||
                event === 'download-jpeg' ||
                event === 'download-png' ||
                event === 'download-png-transparent' ||
                event === 'download-pdf'
            ) {
                downloadTemplate(event);
            } else if (event === 'submit') {
                onSubmitImage();
            }
            setCurrentAction(null);
        }
    };

    const addNewTemplate = () => {
        exportJpegInstance().then((editorInstance: any) => {
            if (editorInstance?.designState && editorInstance?.imageData) {
                const { designState, imageData } = editorInstance;
                const template: any = { ...getEmptyTemplateObj() };
                template.designState = { ...designState };
                template.image = imageData.imageBase64;
                template.entity = editorConfig.entity || 'RESPARK';
                setIsLoading(true);
                uploadJpegCanvasToS3(imageData.imageCanvas)
                    .then((deployedUrl) => {
                        template.image = deployedUrl;
                        return getDesignStateWithDeployedBg(template.designState);
                    })
                    .then((designStateForSave: any) => {
                        template.designState = designStateForSave;
                        return axios.post(
                            `${process.env.REACT_APP_CAMPAING_BASE_URL}`,
                            template,
                            getAxiosHeaders(editorConfig),
                        );
                    })
                    .then((response: any) => {
                        const data = response.data?.data;
                        setIsLoading(false);
                        setActiveTemplateContext({ ...data });
                        setActiveTemplate({ ...data });
                        if (editorConfig.tenantId === 0) {
                            setResparkTemplates((prevTemplates: any[]) => [data, ...prevTemplates]);
                        } else {
                            setTemplatesList((prevTemplates: any[]) => [data, ...prevTemplates]);
                        }
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            }
        });
    };

    const updateTemplate = () => {
        exportJpegInstance().then((editorInstance: any) => {
            if (editorInstance?.designState && editorInstance?.imageData) {
                const { designState, imageData } = editorInstance;
                const template = { ...activeTemplate };
                template.designState = { ...designState };
                template.image = imageData.imageBase64;
                template.entity = editorConfig.entity || 'RESPARK';
                setIsLoading(true);
                uploadJpegCanvasToS3(imageData.imageCanvas)
                    .then((deployedUrl) => {
                        template.image = deployedUrl;
                        return getDesignStateWithDeployedBg(template.designState);
                    })
                    .then((designStateForSave: any) => {
                        template.designState = designStateForSave;
                        return axios.post(
                            `${process.env.REACT_APP_CAMPAING_BASE_URL}`,
                            template,
                            getAxiosHeaders(editorConfig),
                        );
                    })
                    .then((response: any) => {
                        const data = response.data?.data;
                        setIsLoading(false);
                        setActiveTemplateContext({ ...data });
                        setActiveTemplate(data);
                        if (editorConfig.tenantId === 0) {
                            setResparkTemplates((prevTemplates: any[]) =>
                                prevTemplates.map((templateItem: any) =>
                                    templateItem.id === activeTemplate.id ? data : templateItem,
                                ),
                            );
                        } else {
                            setTemplatesList((prevTemplates: any[]) =>
                                prevTemplates.map((templateItem: any) =>
                                    templateItem.id === activeTemplate.id ? data : templateItem,
                                ),
                            );
                        }
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            }
        });
    };

    const onDeleteTemplateClick = (event: any, template: any, templateIndex: number) => {
        setShowTemplateDeleteConfirmation({ active: true, template, templateIndex });
        event.stopPropagation()
    }

    const onEditTemplateClick = (event: any, template: any, templateIndex: number) => {
        event.stopPropagation();
        setShowTemplateEditModal({ active: true, template, templateIndex });
    };

    const handleTemplateEdit = (updatedTemplate: any) => {
        const templatesCopy = [...resparkTemplates];
        templatesCopy[showTemplateEditModal.templateIndex] = {
            ...showTemplateEditModal.template,
            ...updatedTemplate
        };
        setResparkTemplates(templatesCopy);
        getCategories(templatesCopy);
        setShowTemplateEditModal({ active: false, template: {}, templateIndex: null });
    };

    const updateCustomerTemplate = () => {
        setIsLoading(true);
        const template = showTemplateEditModal.template;
        axios.put(`${process.env.REACT_APP_CAMPAING_BASE_URL}/category`,
            {
                id: template.id,
                category: template.category,
                tags: template.tags,
                storeId: template.storeId,
                tenantId: template.tenantId,
                entity: template.entity || "RESPARK",
            },
            getAxiosHeaders(editorConfig)
        ).then(() => {
            handleTemplateEdit(template);
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const deleteCustomerTemplate = () => {
        setIsLoading(true);
        axios.delete(`${process.env.REACT_APP_CAMPAING_BASE_URL}/${showTemplateDeleteConfirmation.template.id}`, getAxiosHeaders(editorConfig)).then((response: any) => {
            setIsLoading(false);
            const deletedTemplateId = showTemplateDeleteConfirmation.template.id;
            if (editorConfig.tenantId === 0) {
                const nextTemplates = resparkTemplates.filter(
                    (templateItem: any) => templateItem.id !== deletedTemplateId,
                );
                setResparkTemplates(nextTemplates);
                getCategories(nextTemplates);
            } else {
                setTemplatesList((prevTemplates: any[]) =>
                    prevTemplates.filter((templateItem: any) => templateItem.id !== deletedTemplateId),
                );
            }
            setShowTemplateDeleteConfirmation({ active: false, template: null, templateIndex: null });
        }).catch((e) => {
            setIsLoading(false);
        });;
    }

    const downloadTemplate = (format: string = 'download-jpeg') => {
        const isPdf = format === 'download-pdf';
        const isPngFamily =
            format === 'download-png' ||
            format === 'download-png-transparent' ||
            isPdf;
        const exportOptions =
            format === 'download-png-transparent'
                ? { extension: 'png', transparentBackground: true }
                : format === 'download-png' || isPdf
                    ? { extension: 'png', transparentBackground: false }
                    : { ...JPEG_EXPORT_OPTIONS };

        // PNG is lossless — exporting at savingPixelRatio (2) on a bg already stored at
        // 2× banner size produced ~4× pixels (~4–5MB). Cap PNG/PDF to 2× banner @ 1× ratio.
        const pngCap = isPngFamily ? getBgCropOutputSize() : null;
        const imageFileInfo = pngCap
            ? { size: { width: pngCap.width, height: pngCap.height } }
            : {};
        const exportPixelRatio = isPngFamily ? 1 : false;

        const downloadNameOptions = {
            bannerType: editorConfig?.type,
            entity: editorConfig?.entity,
            templateId: activeTemplate?.id,
        };

        getEditorInstance(exportOptions, imageFileInfo, exportPixelRatio).then(async (editorInstance: any) => {
            if (editorInstance?.designState && editorInstance?.imageData) {
                const { imageData } = editorInstance;
                const ext =
                    imageData.extension ||
                    (imageData.mimeType?.includes('png') ? 'png' : 'jpeg');

                if (isPdf) {
                    const fileName = buildDownloadFileName({
                        ...downloadNameOptions,
                        extension: 'pdf',
                    });
                    await downloadBannerAsPdf(imageData, fileName);
                } else {
                    const a = document.createElement('a');
                    a.href = imageData.imageBase64;
                    a.download = buildDownloadFileName({
                        ...downloadNameOptions,
                        extension: ext,
                    });
                    a.click();
                }
            }
        });
    };

    const onBgChangeActionSelect = (from: any) => {
        setOldBgImage(bgImage)
        if (from === 'self') uploadNewBgFromLocal();
        else if (from === 'gallery') setShowBgImages(true);
    };

    const uploadNewBgFromLocal = () => {
        let element: any = uploadImgsInput.current;
        element?.click();
    }

    const onUploadLocalBGImage = (e: any) => {
        if (e.target.files && e.target.files.length) {
            var reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]); // read file as data url
            reader.onload = (event: any) => { // called once readAsDataURL is completed
                const dataUrl = event.target!.result;
                openBgCropper(dataUrl, {
                    outputSize: getBgCropOutputSize(),
                });
            }
        }
    }

    const onCropSubmit = () => {
        const croppedImage = cropperRef.current.getCroppedImage();
        croppedImage.then(async (imgUrl: string) => {
            if (imgUrl) {
                onChangeBg(imgUrl);
                if (isFreeFormBannerMode(editorConfig.type)) {
                    setEditorConfig((prev: any) => ({
                        ...prev,
                        frameBgKind: 'image',
                    }));
                }
            }
            setPendingCropOutputSize(null);
            setShowCropper({ active: false, img: "" });
        })
    }

    const onSelectBgImage = (url: any) => {

        // getBase64Image(url, function (base64: any) {
        //     setBgImage(base64)
        // });

        const toDataURL = url => fetch(url)
            .then(response => response.blob())
            .then(blob => new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(blob)
            }))
        toDataURL(url)
            .then((dataUrl) => {
                const imageDataUrl = String(dataUrl);
                if (isFreeFormBannerMode(editorConfig.type)) {
                    const outputSize = getBgCropOutputSize();
                    if (outputSize) {
                        openBgCropper(imageDataUrl, { outputSize });
                    } else {
                        onChangeBg(imageDataUrl);
                        setEditorConfig((prev: any) => ({
                            ...prev,
                            frameBgKind: 'image',
                        }));
                    }
                } else {
                    onChangeBg(imageDataUrl);
                }
            })
    }

    const getEditorInstance = async (
        exportOptions: object = {},
        imageFileInfo: object = {},
        pixelRatio: boolean | number = false,
    ) => {
        const { current: saveFunction } = editedImage;
        if (!saveFunction) return;
        const result = await saveFunction(imageFileInfo, pixelRatio, false, exportOptions);
        return result;
    };

    const textObj = useMemo(
        () => ({
            fill: defaultTextFill,
            stroke: defaultTextFill,
            strokeWidth: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowBlur: 0,
            shadowColor: defaultTextFill,
            shadowOpacity: 1,
            opacity: 1,
            fontFamily: 'Poppins',
            letterSpacing: 0,
            lineHeight: 1,
            align: 'left',
            fontStyle: 'normal',
            name: 'Text',
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            text: 'Respark',
            x: 50,
            y: 40,
            fontSize: 50,
            width: 270,
            height: 55,
        }),
        [defaultTextFill],
    );

    const addTextToEditorInstance = (template: TextTemplateItem) => {
        const { annotations, selectionsIds, textId } = buildTextTemplateInsertion(
            textObj,
            template,
            uiTheme,
        );

        // Preferred path: add the text live via the editor's UPDATE_STATE bridge.
        // This merges the new annotation into the running design state without a full
        // canvas export + editor remount, so adding text is instant and stays undoable.
        const updateState = updateEditorState.current;
        if (typeof updateState === 'function') {
            updateState((state: any) => ({
                isDesignState: true, // record an undo step for the text add
                annotations,
                selectionsIds,
                textIdOfEditableContent: textId,
                toolId: TOOLS.TEXT,
                tabId: TABS.ANNOTATE,
            }));
            setCurrentTab(TABS.ANNOTATE);
            selectEditorTab.current?.(TABS.ANNOTATE);
            setShowAddTextModal(false);
            return;
        }

        // Fallback (bridge not ready yet): legacy export + remount flow.
        getEditorInstance().then((editorInstance: any) => {
            if (editorInstance.designState && editorInstance.imageData) {
                const { designState, imageData } = editorInstance;
                const templateState = { ...activeTemplate };
                designState.annotations = { ...designState.annotations, ...annotations };
                designState.selectionsIds = selectionsIds;
                templateState.designState = { ...designState };
                templateState.image = imageData.imageBase64;
                commitActiveTemplate(templateState);
                setCurrentId('');
                setShowAddTextModal(false);
            }
        });
    };

    const computeScaledImagePlacement = (loadedImg: HTMLImageElement, state: any) => {
        const crop = state.adjustments?.crop || {};
        const shownImageDimensions = state.shownImageDimensions || {};
        const layerWidth = crop.width || shownImageDimensions.width;
        const layerHeight = crop.height || shownImageDimensions.height;
        const layerCropX = crop.x || 0;
        const layerCropY = crop.y || 0;

        const newImgRatio = Math.min(
            1,
            layerWidth / (loadedImg.width + loadedImg.width * ADDED_IMG_SPACING_PERCENT),
            layerHeight / (loadedImg.height + loadedImg.height * ADDED_IMG_SPACING_PERCENT),
        );

        return {
            x: layerCropX + layerWidth / 2 - (loadedImg.width * newImgRatio) / 2,
            y: layerCropY + layerHeight / 2 - (loadedImg.height * newImgRatio) / 2,
            width: loadedImg.width * newImgRatio,
            height: loadedImg.height * newImgRatio,
            scaleX: 1,
            scaleY: 1,
            rotation: 0,
        };
    };

    const mergeImageAnnotation = (state: any, imageSrc: string, loadedImg: HTMLImageElement) => {
        const id: any = Date.now() * Math.random();
        const newImageId = `Image-${parseInt(id, 10)}`;
        const placement = computeScaledImagePlacement(loadedImg, state);

        return {
            isDesignState: true,
            annotations: {
                [newImageId]: {
                    id: newImageId,
                    name: TOOLS.IMAGE,
                    image: imageSrc,
                    opacity: 1,
                    ...placement,
                },
            },
        };
    };

    const addImageToEditorInstance = (imageSrc: string, loadedImg: HTMLImageElement) => {
        const updateState = updateEditorState.current;
        if (typeof updateState === 'function') {
            updateState((state: any) => mergeImageAnnotation(state, imageSrc, loadedImg));
            return;
        }

        getEditorInstance().then((editorInstance: any) => {
            if (editorInstance?.designState) {
                const { designState, imageData } = editorInstance;
                const patch = mergeImageAnnotation(
                    {
                        ...designState,
                        shownImageDimensions: designState.shownImageDimensions,
                        adjustments: designState.adjustments,
                    },
                    imageSrc,
                    loadedImg,
                );
                const template = { ...activeTemplate };
                designState.annotations = { ...designState.annotations, ...patch.annotations };
                template.designState = { ...designState };
                template.image = imageData.imageBase64;
                commitActiveTemplate(template);
            }
        });
    };

    const loadAndAddImage = (imageUrl: string) => {
        const img = new Image();
        img.onload = () => {
            addImageToEditorInstance(img.src, img);
        };
        if (imageUrl.includes('base64') && !imageUrl.startsWith('data:')) {
            fetch(imageUrl)
                .then(response => response.blob())
                .then(blob => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                }))
                .then((dataUrl) => {
                    img.src = dataUrl;
                });
        } else {
            img.src = imageUrl;
        }
    };

    const onSelectGraphicsImage = (image: string) => {
        if (image) {
            loadAndAddImage(image);
        }
    };

    const onUploadLocalGraphicsImage = (e: any) => {
        if (!e.target.files) return;

        setIsImportingGraphics(true);
        const filesArray = Array.from(e.target.files as FileList);
        const filesLength = filesArray.length;
        let completed = 0;

        const finishIfDone = () => {
            completed += 1;
            if (completed === filesLength) {
                setIsImportingGraphics(false);
            }
        };

        filesArray.forEach((file) => {
            if (!file.type.startsWith('image/')) {
                finishIfDone();
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string;
                const img = new Image();
                img.onload = () => {
                    addImageToEditorInstance(dataUrl, img);
                    finishIfDone();
                };
                img.onerror = finishIfDone;
                img.src = dataUrl;
            };
            reader.onerror = finishIfDone;
            reader.readAsDataURL(file);
        });

        e.target.value = '';
    };

    const getDesignStateWithDeployedBg = async (designState: any) => {
        const baseUrl = process.env.REACT_APP_BASE_URL || '';
        const { designState: prepared } = await prepareDesignStateForSave(designState, baseUrl);
        return prepared;
    };

    // Fetch a remote background and convert it to a base64 data URL for the editor.
    const urlToBase64 = (url: string): Promise<string> =>
        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch background (${response.status})`);
                }
                return response.blob();
            })
            .then(
                (blob) =>
                    new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    }),
            );

    const onSubmitImage = () => {
        exportJpegInstance().then((editorInstance: any) => {
            if (!editorInstance?.designState || !editorInstance?.imageData) {
                return;
            }
            const { imageData } = editorInstance;
            if (editorConfig.outputImageType === 'base64') {
                window.parent.postMessage(
                    { type: 'Editor Closed', data: imageData.imageBase64 },
                    window.location.ancestorOrigins[0],
                );
                return;
            }
            uploadJpegCanvasToS3(imageData.imageCanvas)
                .then((data) => {
                    if (window.location.ancestorOrigins.length) {
                        if (editorConfig.outputImageType === 'editor_response') {
                            window.parent.postMessage(
                                {
                                    type: 'Editor Closed',
                                    data: { image: data, templateId: activeTemplate?.id || 0 },
                                },
                                window.location.ancestorOrigins[0],
                            );
                        } else {
                            window.parent.postMessage(
                                { type: 'Editor Closed', data },
                                window.location.ancestorOrigins[0],
                            );
                        }
                    }
                })
                .catch(() => {
                    setIsLoading(false);
                });
        });
    };

    const onClose = () => {
        setShowEditor(showEditor ? false : true);
        window.parent.postMessage({ type: 'Editor Closed', data: '' }, window.location.ancestorOrigins[0]);
        sessionStorage.removeItem('editor__config');
    }

    const filteredPlatformTemplates = useMemo(() => {
        return resparkTemplates.filter((template: any) => {
            if (template.tenantId !== 0) {
                return false;
            }
            const categoryFilter = templateMatchesCategory(
                template?.category,
                templateFilters.category,
            );
            const searchFilter = templateFilters.searchTerm === ''
                || template?.tags?.toLowerCase()?.includes(templateFilters.searchTerm?.toLowerCase())
                || template?.category?.toLowerCase()?.includes(templateFilters.searchTerm?.toLowerCase());
            return categoryFilter && searchFilter;
        });
    }, [resparkTemplates, templateFilters]);

    const editModalCategoryOptions = useMemo(
        () => mergeUniqueSortedCategories(
            templateCategories,
            parseCommaList(showTemplateEditModal.template?.category),
        ),
        [templateCategories, showTemplateEditModal.template?.category],
    );

    const templatesScrollToIndex = useMemo(() => {
        if (currentTab !== TABS.TEMPLATE || activeTemplate?.id == null) {
            return null;
        }
        const items =
            templateType === TEMPLATE_TYPE.USER ? templatesList : filteredPlatformTemplates;
        const idx = items.findIndex((t: any) => t.id === activeTemplate.id);
        return idx >= 0 ? idx : null;
    }, [currentTab, activeTemplate?.id, templateType, templatesList, filteredPlatformTemplates]);

    const isActiveTemplateItem = (template: any) =>
        activeTemplate?.id != null && template?.id === activeTemplate.id;

    const renderTemplateCard = (
        template: any,
        templateIndex: number,
        options: { adminActions?: boolean; userDelete?: boolean } = {},
    ) => {
        const { adminActions = false, userDelete = false } = options;
        return (
            <div
                key={String(template.id ?? template.image ?? templateIndex)}
                id={template.tenantId}
                data-template-index={templateIndex}
                className={`template-details users-template${isActiveTemplateItem(template) ? ' active' : ''}`}
                onClick={() => onSelectTemplate(template)}
            >
                {adminActions ? <>
                    <div className="info-icon-wrap delete" onClick={(e) => onDeleteTemplateClick(e, template, templateIndex)}>
                        <div className="info">Delete template</div>
                        <div className="icon-wrap" ><AiOutlineCloseCircle /></div>
                    </div>
                    <div className="info-icon-wrap edit" onClick={(e) => onEditTemplateClick(e, template, templateIndex)}>
                        <div className="info">Edit template</div>
                        <div className="icon-wrap">✏️</div>
                    </div>
                </> : userDelete ? (
                    <div className="info-icon-wrap" onClick={(e) => onDeleteTemplateClick(e, template, templateIndex)}>
                        <div className="info">Delete template</div>
                        <div className="icon-wrap" ><AiOutlineCloseCircle /></div>
                    </div>
                ) : null}
                <div className="template-id">{template.id}</div>
                <div className="template-thumb-frame">
                    <LazyImage
                        src={template?.image}
                        alt={`Template ${template.id}`}
                        scrollRoot={templatesScrollRoot}
                        waitForScrollRoot
                    />
                </div>
            </div>
        );
    };

    const renderTemplatesScrollList = (
        items: any[],
        options: { adminActions?: boolean; userDelete?: boolean } = {},
    ) => {
        if (items.length === 0) {
            return null;
        }

        return (
            <VirtualizedGrid
                key={showExpandedTemplates ? 'templates-expanded' : 'templates-collapsed'}
                items={items}
                getKey={(template: any) => String(template.id ?? template.image)}
                columnCount={templateColumnCount}
                estimatedRowHeight={templateEstimatedRowHeight}
                rowGap={templateRowGap}
                scrollElement={templatesScrollRoot}
                measureRows={showExpandedTemplates}
                overscan={2}
                scrollToItemIndex={templatesScrollToIndex}
                renderItem={(template: any, templateIndex: number) =>
                    renderTemplateCard(template, templateIndex, options)
                }
            />
        );
    };

    const RenderTemplatesList = () => {
        return <div
            className={`respark_sidebar-options-panel templates-list-wrap${showExpandedTemplates ? ' active' : ''}`}
            style={{ '--respark-template-thumb-aspect-ratio': templateThumbAspectRatio } as React.CSSProperties}
        >
            <div className="respark_sidebar-options-card">
                <div className="respark_templates-panel-banner">
                    <div className="respark_templates-panel-banner__top">
                        <div
                            className="respark_templates-source-toggle"
                            role="tablist"
                            aria-label="Template library"
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={templateType === TEMPLATE_TYPE.PLATFORM}
                                className={`respark_templates-source-btn ${templateType === TEMPLATE_TYPE.PLATFORM ? 'active' : ''}`}
                                onClick={() => setTemplateType(TEMPLATE_TYPE.PLATFORM)}
                            >
                                Recommended
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={templateType === TEMPLATE_TYPE.USER}
                                className={`respark_templates-source-btn ${templateType === TEMPLATE_TYPE.USER ? 'active' : ''}`}
                                onClick={() => setTemplateType(TEMPLATE_TYPE.USER)}
                            >
                                Your creations
                            </button>
                        </div>
                        {showExpandedTemplates && (
                            <div className="respark_templates-expanded-filters--inline">
                                <div className="filter search-filter">
                                    <input
                                        id="search-input"
                                        placeholder="Search template..."
                                        type="text"
                                        value={templateFilters.searchTerm}
                                        onChange={(e) => setTemplateFilters({ ...templateFilters, searchTerm: e.target.value })}
                                    />
                                    <div
                                        className="icon"
                                        onClick={() => Boolean(templateFilters.searchTerm) ? setTemplateFilters({ ...templateFilters, searchTerm: '' }) : document.getElementById('search-input')?.focus()}
                                    >
                                        {Boolean(templateFilters.searchTerm) ? <AiOutlineCloseCircle /> : <BiSearchAlt />}
                                    </div>
                                </div>
                                <div className="filter category-filter">
                                    <span className="respark_templates-filter-label">Category</span>
                                    <select
                                        className="category-select"
                                        value={templateFilters.category}
                                        onChange={(e) => setTemplateFilters({ ...templateFilters, category: e.target.value })}
                                    >
                                        <option value="All">All</option>
                                        {templateCategories.map((c: any) => (
                                            <option value={c} key={c}>{c}</option>
                                        ))}
                                    </select>
                                    {templateFilters?.category !== 'All' && (
                                        <div
                                            className="icon"
                                            onClick={() => setTemplateFilters({ ...templateFilters, category: 'All' })}
                                        >
                                            <AiOutlineCloseCircle />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <button
                            type="button"
                            className="respark_templates-expand-btn"
                            title={showExpandedTemplates ? 'Collapse templates' : 'Expand templates'}
                            aria-label={showExpandedTemplates ? 'Collapse templates' : 'Expand templates'}
                            onClick={() => setShowExpandedTemplates(!showExpandedTemplates)}
                        >
                            {!showExpandedTemplates ? <HiOutlineArrowsExpand /> : <AiOutlineCloseCircle />}
                        </button>
                    </div>

                    {editorConfig.tenantId === 0 && templateType === TEMPLATE_TYPE.PLATFORM && (
                        <div className="respark_sidebar-category-row respark_templates-category-row">
                            <div className={`categories-list ${showExpandedTemplates ? "expanded" : ""}`}>
                                <div
                                    className={`category-chip ${templateFilters.category === 'All' ? 'active' : ''}`}
                                    onClick={() => setTemplateFilters({ ...templateFilters, category: 'All' })}
                                >All</div>
                                {templateCategories.map((category) => (
                                    <div
                                        onClick={() => setTemplateFilters({ ...templateFilters, category })}
                                        key={category}
                                        className={`category-chip ${templateFilters.category === category ? 'active' : ''}`}
                                    >
                                        <span>{category}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className='templates-by-type-wrap'>
                    {templateType === TEMPLATE_TYPE.USER && <>
                        {templatesList.length > 0 ? (
                            <div
                                className="templates-list"
                                ref={setTemplatesScrollRoot}
                            >
                                {renderTemplatesScrollList(templatesList, { userDelete: true })}
                            </div>
                        ) : <div className='empty-wrap'>{isLoading ? <>Loading...</> : <>Templates not found</>}</div>}
                    </>}

                    {templateType === TEMPLATE_TYPE.PLATFORM && <>
                        {filteredPlatformTemplates.length > 0 ? (
                            <div
                                className="templates-list"
                                ref={setTemplatesScrollRoot}
                            >
                                {renderTemplatesScrollList(filteredPlatformTemplates, {
                                    adminActions: editorConfig.tenantId === 0,
                                })}
                            </div>
                        ) : <div className='empty-wrap'>{isLoading ? <>Loading...</> : <>Templates not found</>}</div>}
                    </>}
                </div>

                {!showExpandedTemplates && <div className='btn-wrap bg-change'>
                    <div className="btn" onClick={() => onSelectTemplate(null)}>Create New Template<RiImageAddFill2 /></div>
                </div>}
            </div>
        </div>
    }

    const renderBackgroundsList = () => {
        const typeBackgrounds = editorConfig.type ? BACKGROUND[editorConfig.type] : undefined;
        const backgroundItems = flattenBackgroundImages(typeBackgrounds, activeBgImgCategory);

        return (
            <div
                className="respark_sidebar-options-panel backdrop-modal-content d-f-c background-imges-wrap"
                ref={(el) => {
                    bgImageModalRef.current = el;
                }}
            >
                <div className="respark_sidebar-options-card">
                    <div className="respark_background-panel-banner">
                        <div className="respark_sidebar-category-row">
                            <div className="categories-list" role="tablist" aria-label="Background categories">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeBgImgCategory === 'All'}
                                    className={`category-chip ${activeBgImgCategory === 'All' ? 'active' : ''}`}
                                    onClick={() => setActiveBgImgCategory('All')}
                                >
                                    All
                                </button>
                                {typeBackgrounds && Object.keys(typeBackgrounds)?.map((imageCategory) => (
                                    <button
                                        type="button"
                                        role="tab"
                                        key={imageCategory}
                                        aria-selected={activeBgImgCategory === imageCategory}
                                        title={imageCategory}
                                        className={`category-chip ${activeBgImgCategory === imageCategory ? 'active' : ''}`}
                                        onClick={() => setActiveBgImgCategory(imageCategory)}
                                    >
                                        <span>{imageCategory}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="modal-containt" ref={setBgScrollRoot}>
                        <div className="category-image-wrap">
                            <div className="image-category-wrap">
                                <div className="bg-images-list-wrap">
                                    <VirtualizedGrid
                                        items={backgroundItems}
                                        getKey={(item) => item.key}
                                        columnCount={2}
                                        estimatedRowHeight={120}
                                        scrollElement={bgScrollRoot}
                                        renderItem={(item) => (
                                            <div
                                                className={`img-wrap ${bgImage === item.src ? 'active' : ""}`}
                                                onClick={() => onSelectBgImage(item.src)}
                                            >
                                                <LazyImage
                                                    src={item.src}
                                                    alt="Background option"
                                                    scrollRoot={bgScrollRoot}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="btn-wrap bg-change">
                        <div className="btn" onClick={() => onBgChangeActionSelect('self')}>
                            Upload from your computer<RiImageAddFill2 />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderImagesList = () => {
        const closeGraphicsPanel = () => {
            setCurrentTab(TABS.ANNOTATE);
            selectEditorTab.current?.(TABS.ANNOTATE);
        };

        return (
            <GraphicsGalleryPanel
                className="respark_sidebar-options-panel graphics-images-sidebar"
                activeCategory={activeGraphicsCategory}
                setActiveCategory={setActiveGraphicsCategory}
                onSelectImage={onSelectGraphicsImage}
                onUploadClick={() => uploadGraphicsInput.current?.click()}
                uploadInputRef={uploadGraphicsInput}
                onUploadChange={onUploadLocalGraphicsImage}
                isImporting={isImportingGraphics}
                onCancel={closeGraphicsPanel}
                onClose={closeGraphicsPanel}
            />
        );
    }

    const handlAddItem = (item: string, from: string) => {
        const trimmed = item.trim();
        if (!trimmed) {
            return;
        }

        const currentItems = parseCommaList(showTemplateEditModal.template[from]);

        if (currentItems.some((value) => value.toLowerCase() === trimmed.toLowerCase())) {
            return;
        }

        const nextValue = [...currentItems, trimmed].join(',');

        setShowTemplateEditModal({
            ...showTemplateEditModal,
            template: {
                ...showTemplateEditModal.template,
                [from]: nextValue,
            },
        });

        if (from === 'category') {
            setTemplateCategories((prev) => mergeUniqueSortedCategories(prev, [trimmed]));
        }
    }

    return (
        <>
            <div
                className={`editor-component-wrap ${uiThemeClassName(uiTheme)}${showBgImages || showImagesPanel ? ' modal-opened' : ''}${currentTab === TABS.IMAGE_GALLERY ||
                        (!isFreeFormEditor &&
                            (currentTab === TABS.TEMPLATE || currentTab === TABS.BACKGROUND))
                        ? ' respark-wrapper-options-visible'
                        : ''
                    }${currentTab == null ? ' respark-wrapper-tab-deselected' : ''}${!isFreeFormEditor && showExpandedTemplates ? ' respark-templates-expanded' : ''}`}
            >
                <div className="editor-container ">
                    {editorConfig.type ? <>
                        <div className='editor-wrapper'>
                            <div className='editor-wrap' >
                                <div className='editor-root' ref={editorWrapRef}>
                                    {!isFreeFormEditor && currentTab === TABS.TEMPLATE && RenderTemplatesList()}
                                    {!isFreeFormEditor && showBgImages && renderBackgroundsList()}
                                    {showImagesPanel && renderImagesList()}
                                    {showEditor && (
                                        <ImageEditor
                                            key={editorKey}
                                            source={bgImage}
                                            onSave={(editedImageObject, designState) => {
                                                // setCurrentAction({type:'',action:''});
                                            }}
                                            loadableDesignState={activeTemplate?.designState ?? {}}
                                            onBeforeSave={() => false}
                                            savingPixelRatio={2}
                                            previewPixelRatio={window.devicePixelRatio}
                                            onClose={onClose}
                                            getCurrentImgDataFnRef={editedImage}
                                            updateStateFnRef={updateEditorState}
                                            selectTabFnRef={selectEditorTab}
                                            keyboardShortcutHandlersRef={keyboardShortcutHandlersRef}
                                            annotationsCommon={{
                                                fill: defaultTextFill,
                                            }}
                                            Text={{
                                                text: 'Add text here',
                                                fontFamily: 'Poppins',
                                                fonts: TEXT_FONTS,
                                                fontSize: 40,
                                                letterSpacing: 0,
                                                lineHeight: 1,
                                                align: 'left',
                                                fontStyle: 'normal',
                                                fill: defaultTextFill,
                                            }}
                                            Crop={{ noPresets: true }}
                                            Watermark={waterMarks ? { gallery: [waterMarks] } : { gallery: [] }}
                                            tabsIds={sidebarTabsIds}
                                            logo={editorConfig.logo}
                                            defaultTabId={currentTab ?? undefined}
                                            // Override vendored defaultConfig (CROP) — null = no tool on boot;
                                            // avoids CropTransformer selection box on the background.
                                            defaultToolId={null}
                                            uiTheme={uiTheme}
                                            bannerFrameControls={bannerFrameControls}
                                            onModify={handleFreeFormDesignModify}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <HiddenUploadInput
                            ref={uploadImgsInput}
                            onChange={onUploadLocalBGImage}
                            multiple
                        />
                        <Backdrop
                            className="backdrop-modal-wrapper cropper-modal"
                            open={showCropper.active ? true : false}
                        >
                            <div className="backdrop-modal-content d-f-c "
                                style={{
                                    height: `auto`,
                                    width: `${editorConfig.width + 500}px`
                                }}>
                                <div className="modal-close" onClick={() => setShowCropper({ active: false, img: "" })}><AiOutlineCloseCircle /></div>
                                <div className="heading">Crop Selected Image</div>
                                <div className="modal-containt">
                                    <ImageCropper
                                        ref={cropperRef}
                                        editorConfig={editorConfig}
                                        IMG={showCropper.img}
                                        freeAspect={false}
                                        outputSize={pendingCropOutputSize}
                                    />
                                    <div className="btn-wrap">
                                        <div className="btn" onClick={() => onBgChangeActionSelect('self')}>Change Image</div>
                                        <div className="btn" onClick={onCropSubmit}>Crop Image</div>
                                    </div>
                                </div>
                            </div>
                        </Backdrop>
                        <Backdrop
                            className="backdrop-modal-wrapper confirmation-modal"
                            open={showTemplateChangeConfirmation.active ? true : false}
                        >
                            <div className="backdrop-modal-content d-f-c-c respark_confirmation-modal-content">
                                <div className="modal-close" onClick={() => setShowTemplateChangeConfirmation({ active: false, template: {}, userAgreeToChange: false })}><AiOutlineCloseCircle /></div>
                                <div className="heading">Change template</div>
                                <div className="modal-containt">
                                    <div className={`message ${showTemplateChangeConfirmation.userAgreeToChange ? " active" : ""}`} onClick={() => setShowTemplateChangeConfirmation({ ...showTemplateChangeConfirmation, userAgreeToChange: !showTemplateChangeConfirmation.userAgreeToChange })}>
                                        <div className={`checkbox`}></div>
                                        Don&apos;t show again
                                    </div>
                                    <div className="warning-msg">Please make sure that all current template changes will be reverted and this action cannot be undone.</div>
                                    <div className="btn-wrap">
                                        <div className="btn" onClick={replaceTemplate}>Replace current template</div>
                                    </div>
                                </div>
                            </div>
                        </Backdrop>
                        <Backdrop
                            className="backdrop-modal-wrapper confirmation-modal"
                            open={showTemplateDeleteConfirmation.active ? true : false}
                        >
                            <div className="backdrop-modal-content d-f-c-c respark_confirmation-modal-content">
                                <div className="modal-close" onClick={() => setShowTemplateDeleteConfirmation({ active: false, template: {}, userAgreeToChange: false, templateIndex: null })}><AiOutlineCloseCircle /></div>
                                <div className="heading">Delete template</div>
                                <div className="modal-containt">
                                    <div className={`message ${showTemplateDeleteConfirmation.userAgreeToChange ? " active" : ""}`} onClick={() => setShowTemplateDeleteConfirmation({ ...showTemplateDeleteConfirmation, userAgreeToChange: !showTemplateDeleteConfirmation.userAgreeToChange })}>
                                        <div className={`checkbox`}></div>
                                        Don&apos;t show again
                                    </div>
                                    <div className="warning-msg">Please make sure that this action cannot be undone.</div>
                                    <div className="btn-wrap">
                                        <div className="btn" onClick={deleteCustomerTemplate}>Delete current template</div>
                                    </div>
                                </div>
                            </div>
                        </Backdrop>
                        <Backdrop
                            className="backdrop-modal-wrapper confirmation-modal edit-template-modal"
                            open={showTemplateEditModal.active ? true : false}
                        >
                            <div className="backdrop-modal-content respark_edit-template-modal d-f-c">
                                <div className="respark_edit-template-modal__header">
                                    <div className="heading">Edit template</div>
                                    <button
                                        type="button"
                                        className="modal-close"
                                        aria-label="Close"
                                        onClick={() => setShowTemplateEditModal({ active: false, template: {}, templateIndex: null })}
                                    >
                                        <AiOutlineCloseCircle />
                                    </button>
                                </div>
                                <div className="modal-containt">
                                    <div className="template-preview">
                                        <img src={showTemplateEditModal.template.image} alt="Template preview" />
                                    </div>
                                    <div className="form-group">
                                        <label>Category</label>
                                        <div className="category-input-wrap">
                                            <MultiSelect
                                                from="category"
                                                value={showTemplateEditModal.template.category || ''}
                                                options={editModalCategoryOptions}
                                                onChange={(value) => setShowTemplateEditModal({
                                                    ...showTemplateEditModal,
                                                    template: {
                                                        ...showTemplateEditModal.template,
                                                        category: value
                                                    }
                                                })}
                                            />
                                            <input
                                                type="text"
                                                className="respark_edit-template-tag-input"
                                                placeholder="Type a category and press Enter"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.currentTarget.value.trim() !== '') {
                                                        handlAddItem(e.currentTarget.value, 'category');
                                                        e.currentTarget.value = '';
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Tags</label>
                                        <div className="category-input-wrap">
                                            <MultiSelect
                                                from="tag"
                                                value={showTemplateEditModal.template.tags || ''}
                                                options={TAGS || []}
                                                onChange={(value) => setShowTemplateEditModal({
                                                    ...showTemplateEditModal,
                                                    template: {
                                                        ...showTemplateEditModal.template,
                                                        tags: value
                                                    }
                                                })}
                                            />
                                            <input
                                                type="text"
                                                className="respark_edit-template-tag-input"
                                                placeholder="Type a tag and press Enter"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.currentTarget.value.trim() !== '') {
                                                        handlAddItem(e.currentTarget.value, 'tags');
                                                        e.currentTarget.value = '';
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="respark_edit-template-modal__footer">
                                    <button
                                        type="button"
                                        className="respark_edit-template-modal__btn respark_edit-template-modal__btn--cancel"
                                        onClick={() => setShowTemplateEditModal({ active: false, template: {}, templateIndex: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="respark_edit-template-modal__btn respark_edit-template-modal__btn--save"
                                        onClick={updateCustomerTemplate}
                                    >
                                        Save changes
                                    </button>
                                </div>
                            </div>
                        </Backdrop>
                        <TextTemplatePickerModal
                            open={Boolean(showAddTextModal)}
                            uiTheme={uiTheme}
                            contentRef={testStyleModalRef}
                            onClose={() => {
                                setShowAddTextModal(false);
                                setCurrentId('');
                            }}
                            onSelect={(template) => addTextToEditorInstance(template)}
                        />

                    </> : <>
                        <Spinner />
                    </>}
                    {isLoading && <Spinner />}
                </div>
            </div>
        </>
    );
}
export default memo(Editor);
